import { faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import { addNotification } from '@redux/features/notificationsSlice';
import { payForEntry } from '@redux/features/entriesSlice';
import { formatDate } from 'helpers/formatDate';
import { isNotToday } from 'helpers/isNotToday';
import { scrollDate } from 'helpers/scrollDate';
import { MoneyAmount } from 'components/MoneyAmount';
import { DatePicker } from 'components/DatePicker';
import { Checkbox } from 'components/Checkbox';
import { Dropdown } from 'components/Dropdown';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import cn from 'classnames';
import styles from '../PersonCell.scss';

export const PaymentForm = ({ bookingDate, payment, balance, onReset }) => {
  const { paymentMethods } = useSelector((state) => state.paymentMethods);
  const { locations } = useSelector((state) => state.locations);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [amount, setAmount] = useState(payment.amount - payment.paidAmount);
  const [customDate, setCustomDate] = useState(formatDate(new Date()));
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [isUseBalance, setIsUseBalance] = useState(false);
  const [addOverpayToBalance, setAddOverpayToBalance] = useState(false);
  const query = useSearchParamsUpdater();
  const { locationName } = useParams();
  const dispatch = useDispatch();

  const locationTransliteration = query.getParam('location') || locationName;

  const remainingAmount = payment.amount - payment.paidAmount;
  const overpayAmount = Number(amount) > remainingAmount ? Number(amount) - remainingAmount : 0;
  const hasOverpay = overpayAmount > 0;

  const locationID = useMemo(
    () => locations.find(({ transliteration }) => transliteration === locationTransliteration)?.id,
    [locations, locationTransliteration]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isUseBalance && (!selectedMethod || Number(amount) <= 0 || !locationID)) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Заповніть усі поля',
          type: 'error',
        })
      );
      return;
    }

    await dispatch(
      payForEntry({
        id: payment.id,
        method: isUseBalance ? 'balance' : { id: selectedMethod.id, value: selectedMethod.value },
        cashboxID: isUseBalance ? null : selectedMethod.cashbox.id,
        amount: Number(amount),
        locationID,
        addToBalance: hasOverpay && addOverpayToBalance,
        overpayAmount: hasOverpay && addOverpayToBalance ? overpayAmount : 0,
        ...(showCustomDate ? { date: customDate } : {}),
      })
    );

    onReset();
    setSelectedMethod(null);
    setAmount(remainingAmount);
    setAddOverpayToBalance(false);
  };

  const handleDateChange = useCallback(
    (direction) => setCustomDate((prev) => scrollDate(prev, direction)),
    [setCustomDate]
  );

  const formattedPaymentMethods = useMemo(() => {
    if (!locationTransliteration) return paymentMethods;

    return paymentMethods.filter(
      (m) => m.value !== 'cash' || m.cashbox?.location?.transliteration === locationTransliteration
    );
  }, [paymentMethods, locationTransliteration]);

  useEffect(() => {
    if (isUseBalance) {
      setAmount(Math.min(balance, remainingAmount));
    } else {
      setAmount(payment.amount - payment.paidAmount);
    }
  }, [isUseBalance, balance, remainingAmount]);

  useEffect(() => {
    if (!hasOverpay) {
      setAddOverpayToBalance(false);
    }
  }, [hasOverpay]);

  return (
    <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
      {balance > 0 && (
        <div className={styles.block}>
          Доступний баланс: <MoneyAmount value={balance} />
          <Checkbox
            className={styles.checkbox}
            checked={isUseBalance}
            onChange={() => setIsUseBalance(() => !isUseBalance)}
            label='Зняти з балансу'
            smallStyle
          />
        </div>
      )}
      <div className={styles.block}>
        <div className={cn(styles.form, { [styles.disabled]: isUseBalance })}>
          <label>Сума оплати</label>
          <div className={styles.inputRow}>
            <Input
              className={cn(styles.input, styles.amount)}
              type='number'
              step={100}
              name='amount'
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder='Сума оплати'
            />
            / <MoneyAmount value={remainingAmount} />
          </div>

          {hasOverpay && (
            <>
              <p>
                ⚠️ Введена сума перевищує рахунок на <MoneyAmount value={overpayAmount} />
              </p>
              <Checkbox
                className={cn(styles.checkbox, styles.balanceCheckbox)}
                checked={addOverpayToBalance}
                onChange={() => setAddOverpayToBalance((prev) => !prev)}
                label='Зарахувати на баланс клієнта'
                smallStyle
              />
            </>
          )}

          <label>Спосіб оплати</label>
          <Dropdown
            className={styles.dropdown}
            options={formattedPaymentMethods}
            value={selectedMethod}
            onChange={setSelectedMethod}
            placeholder='Спосіб оплати'
          />

          {isNotToday(formatDate(query.getParam('date'))) && (
            <>
              <Checkbox
                className={styles.checkbox}
                label='Обрати дату оплати'
                checked={showCustomDate}
                onChange={() => setShowCustomDate((prev) => !prev)}
                smallStyle
              />

              {showCustomDate && (
                <DatePicker
                  className={styles.customDate}
                  date={customDate}
                  minDate={bookingDate}
                  maxDate={formatDate(new Date())}
                  handleScrollDate={handleDateChange}
                  minDateTooltip='Оплату не можна провести раніше дати заняття'
                  maxDateTooltip='Оплату не можна провести майбутнім числом'
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className={cn(styles.block, styles.paidInfo)}>
        Внесені кошти:{' '}
        <span className={styles.paidMoney}>
          <MoneyAmount value={payment.paidAmount} /> / <MoneyAmount value={payment.amount} />
        </span>
      </div>

      <Button className={styles.modalButton} type='submit' noStyle>
        <FontAwesomeIcon icon={faMoneyBillWave} />
        Внести оплату
      </Button>
    </form>
  );
};
